import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { contains, filter, map, size } from 'underscore';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { withCustomValidation, withError } from '@bingads-webui-react/validation';
import { Checkbox, Label, Button, makeStyles, mergeClasses, shorthands, tokens, useId } from '@fluentui/react-components';

export const useStyles = makeStyles({
  targetContainer: {
    '& .dimensions': {
      display: 'grid',
      gridTemplateColumns: 'auto auto auto',
      columnGap: '10px',
      ...shorthands.padding('2px'),

      '& input[type="checkbox"]': {
        ...shorthands.margin('8px'),
      },
      '& label': {
        flexGrow: 1,
      },
    },
  },
  toggleButton: {
    color: tokens.colorBrandForegroundLink,
    justifyContent: 'flex-start',
  },
});

const getErrorMessage = ({ count, i18n }) => curlyTemplate(i18n.getString(_TL_('At least {{count}} target(s) should to be selected.')))({
  count,
});

const Target = ({
  i18n,
  title,
  description,
  formatter,
  dimensions,
  availableDimensions,
  minSelection,
  validationProps,
  updateValidation,
  hideDeselectAll,
  hideSelectAll,
  onRenderLabel,
  targetClasses,
  checkBoxClass,
  containerClass,
  checkedClass,
  disabledClass,
  disabledDimensions,
  defaultSelectAll,
  size: optionSize,
  renderLabel,
  logger,
  ariaLabel,
}) => {
  const classes = useStyles();

  const selectedAll = size(dimensions) === size(availableDimensions);

  const onToggleDimension = useCallback((dimension) => {
    let newDimensions;
    if (contains(dimensions, dimension)) {
      newDimensions = filter(dimensions, d => d !== dimension);
    } else {
      newDimensions = [...dimensions, dimension];
    }

    updateValidation(newDimensions.length >= minSelection ? [] : [getErrorMessage({ i18n, count: minSelection })], {
      [validationProps.field]: newDimensions,
    });

    if (logger) {
      logger.log(`onTargetChange - ${validationProps.field} - ${newDimensions}`);
    }
  }, [dimensions, updateValidation, minSelection, i18n, validationProps.field, logger]);

  const onToggleAll = useCallback(() => {
    if (size(dimensions) < size(availableDimensions)) {
      updateValidation([], {
        [validationProps.field]: Object.keys(availableDimensions),
      });
    } else {
      updateValidation([getErrorMessage({ i18n, count: minSelection })], {
        [validationProps.field]: [],
      });
    }
  }, [availableDimensions, dimensions, i18n, minSelection, updateValidation, validationProps.field]);

  useEffect(() => {
    if (defaultSelectAll) {
      updateValidation([], {
        [validationProps.field]: Object.keys(availableDimensions),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectAll]);

  const descriptionId = useId('description');
  const dimensionId = useId('dimension');
  const labelById = description ? descriptionId : dimensionId;

  return (
    <div className={mergeClasses(classes.targetContainer, containerClass, 'target-container')} role="group" aria-labelledby={labelById}>
      {title && <Label>{title}</Label>}
      {description && <div id={descriptionId}>{description}</div>}
      {((!hideDeselectAll && selectedAll) || (!hideSelectAll && !selectedAll)) &&
        disabledDimensions.length === 0 &&
        <Button
          appearance="transparent"
          onClick={onToggleAll}
          className={mergeClasses(classes.toggleButton, 'toggle-button')}
        >
          {selectedAll ? i18n.getString(_TL_('Deselect all')) : i18n.getString(_TL_('Select all'))}
        </Button>
      }
      <div id={dimensionId} className="dimensions" aria-label={ariaLabel}>
        {map(availableDimensions, (_, dimension) => {
          const checked = contains(dimensions, dimension);
          const disabled = contains(disabledDimensions, dimension);
          const label = renderLabel ? renderLabel({
            label: formatter(dimension, i18n),
            selected: checked,
            disabled,
            dimension,
            }) :
            formatter(dimension, i18n);

          return (
            <Checkbox
              key={dimension}
              className={mergeClasses(
                checkBoxClass,
                targetClasses[dimension],
                checked ? 'is-checked' : 'is-unchecked',
                disabled ? 'is-disabled' : 'is-enabled',
                checked && checkedClass,
                disabled && disabledClass
              )}
              disabled={disabled}
              label={label}
              checked={checked}
              onChange={() => onToggleDimension(dimension)}
              onRenderLabel={onRenderLabel}
              size={optionSize}
            />
        );
        })}
      </div>
    </div>
  );
};

Target.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  formatter: PropTypes.func.isRequired,
  dimensions: PropTypes.arrayOf(PropTypes.string),
  availableDimensions: PropTypes.shape().isRequired,
  validationProps: PropTypes.shape({
    field: PropTypes.string.isRequired,
  }).isRequired,
  updateValidation: PropTypes.func.isRequired,
  hideDeselectAll: PropTypes.bool,
  hideSelectAll: PropTypes.bool,
  onRenderLabel: PropTypes.func,
  targetClasses: PropTypes.shape({}),
  containerClass: PropTypes.string,
  checkBoxClass: PropTypes.string,
  checkedClass: PropTypes.string,
  disabledClass: PropTypes.string,
  disabledDimensions: PropTypes.arrayOf(PropTypes.string),
  defaultSelectAll: PropTypes.bool,
  size: PropTypes.string,
  renderLabel: PropTypes.func,
  logger: PropTypes.shape({
    log: PropTypes.func,
  }),
  ariaLabel: PropTypes.string,
  minSelection: PropTypes.number,
};

Target.defaultProps = {
  title: undefined,
  description: undefined,
  dimensions: [],
  hideDeselectAll: false,
  hideSelectAll: false,
  onRenderLabel: undefined,
  targetClasses: {},
  containerClass: '',
  checkBoxClass: '',
  checkedClass: '',
  disabledClass: '',
  disabledDimensions: [],
  defaultSelectAll: false,
  size: 'large',
  renderLabel: undefined,
  logger: undefined,
  ariaLabel: undefined,
  minSelection: 1,
};

const validatedTarget = withError(withCustomValidation(Target));

export { validatedTarget as Target };
